import { Loader, RefreshCw, Sparkles } from 'lucide-react';

import { cn } from '../../utils/cn';

import { RANGE_PRESETS, type SessionRange } from './date-range';

interface SessionRangeToolbarProps {
  range: SessionRange;
  today: string;
  activePreset: string | null;
  onChange: (patch: Partial<SessionRange>) => void;
  onPreset: (days: number) => void;
  batch: { count: number; limit: number; isRunning: boolean; onRun: () => void };
  sync: { isRunning: boolean; onRun: () => void };
}

export const SessionRangeToolbar = ({
  range,
  today,
  activePreset,
  onChange,
  onPreset,
  batch,
  sync,
}: SessionRangeToolbarProps) => (
  <div className="flex items-center gap-3 flex-wrap">
    <span className="text-sm font-medium text-slate-700">Tidsperiod:</span>

    <div className="flex items-center gap-1">
      {RANGE_PRESETS.map((preset) => (
        <button
          key={preset.key}
          type="button"
          onClick={() => onPreset(preset.days)}
          className={cn(
            'rounded-lg border px-3 py-2 text-sm transition',
            activePreset === preset.key
              ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
              : 'border-slate-300 text-slate-700 hover:bg-slate-50',
          )}
        >
          {preset.label}
        </button>
      ))}
    </div>

    <div className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5">
      <input
        type="date"
        aria-label="Från datum"
        value={range.fromDate}
        max={range.toDate}
        onChange={(event) => onChange({ fromDate: event.target.value })}
        className="text-sm focus:outline-none"
      />
      <input
        type="time"
        aria-label="Från klockslag"
        value={range.fromTime}
        onChange={(event) => onChange({ fromTime: event.target.value })}
        className="text-sm text-slate-600 focus:outline-none"
      />
      <span className="text-slate-400">→</span>
      <input
        type="date"
        aria-label="Till datum"
        value={range.toDate}
        min={range.fromDate}
        max={today}
        onChange={(event) => onChange({ toDate: event.target.value })}
        className="text-sm focus:outline-none"
      />
      <input
        type="time"
        aria-label="Till klockslag"
        value={range.toTime}
        onChange={(event) => onChange({ toTime: event.target.value })}
        className="text-sm text-slate-600 focus:outline-none"
      />
    </div>

    <button
      type="button"
      onClick={batch.onRun}
      disabled={batch.isRunning || batch.count === 0}
      className="ml-auto flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:bg-slate-300 transition-colors text-sm font-medium"
      title={
        batch.count === 0
          ? 'Alla sessioner är redan analyserade'
          : `Kör analyst på upp till ${batch.limit} oanalyserade sessioner (${batch.count} kvar)`
      }
    >
      {batch.isRunning ? (
        <Loader className="w-4 h-4 animate-spin" />
      ) : (
        <Sparkles className="w-4 h-4" />
      )}
      Analysera batch ({Math.min(batch.count, batch.limit)})
    </button>

    <button
      type="button"
      onClick={sync.onRun}
      disabled={sync.isRunning}
      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 transition-colors text-sm font-medium"
    >
      <RefreshCw className={cn('w-4 h-4', sync.isRunning && 'animate-spin')} />
      Synkronisera
    </button>
  </div>
);
